import $ from "jquery";
import {gsap} from "gsap/all";
import {Action} from "./Action";
import {cloneSvg, setFillColors} from "../SvgBank";

// Press the button while the shrinking ring is inside the success zone. Each
// press in the config runs one after the other: the outer ring shrinks from
// full size down to the inner colored circle over its duration. Once the ring
// enters the black zone circle, the zone turns green and the right button
// succeeds; pressing too early, pressing the wrong button, or letting the
// ring close all the way to the colored circle fails, turning the zone black
// again. A press without a duration is untimed: the ring hides, the zone
// stays black and any correct button press is valid whenever it comes.
// A press can also carry its own instruction and subtext, which replace the
// top instructions once the press before it resolves.
// One point per press.
export class TimingAction extends Action {

  constructor(game, config) {
    super(game, config);

    // How far the ring has shrunk, as a share of its full size. Both values
    // come from the circles of the design file.
    // Where the shrink ends, on the colored button in the middle.
    this.innerRatio = 0.5;
    // Where the ring reaches the zone and the press starts counting.
    this.successRatio = 0.79;

    this.presses = config.presses || [];
    this.maxPoints = this.presses.length;

    this.pressIndex = -1;
    this.waitingForPress = false;
    this.inWindow = false;
    this.shrinkUnlocked = false;

    this.$presses = null;
    this.shrinkTween = null;
    this.holdCall = null;
  }

  // The buttons respond as soon as the action shows up, so an early press
  // counts (and fails on a timed press) instead of being swallowed. Only the
  // ring shrink waits for the game's start delay.
  prestart() {
    this.nextPress();
  }

  start() {
    this.shrinkUnlocked = true;

    // The first press is already waiting, launch its shrink now if it has one
    // and was not resolved during the wait.
    const press = this.presses[this.pressIndex];
    if (this.waitingForPress && press && press.duration !== undefined) {
      this.startShrink(press, this.$presses.eq(this.pressIndex));
    }
  }

  build() {
    const $wrap = $('<div class="timing-presses"></div>');

    this.presses.forEach((press, i) => {
      if (i > 0) $wrap.append($('<div class="press-arrow"></div>').append(cloneSvg("spacer-arrow")));

      const button = this.buttonById(press.button);

      // The key over the button is dev help to know which one to press, shown
      // while the settings ask for it.
      const devKey = this.app.getShowConsoleKeys()
        ? `<div class="dev-key">${this.app.keypressForAction(press.button)}</div>`
        : "";

      // The number over the button is the rank of the press in the sequence,
      // so the order to follow reads at a glance. A lone press has no order to
      // give away, so it goes without.
      const number = this.presses.length > 1
        ? `<div class="number">${i + 1}</div>`
        : "";

      const $press = $(`
        <div class="timing-press stacked-button">
          <div class="base"></div>
          ${number}
          <div class="state success"></div>
          <div class="state fail"></div>
          ${devKey}
        </div>
      `);

      // An untimed press never shows the shrinking ring, hidden from the
      // start so it does not flash while the press waits its turn.
      if (press.duration === undefined) $press.addClass("untimed");

      // In the button SVG the solid stroked circle is the shrinking ring (the
      // dashed one just outlines the zone), the black circle is the success
      // zone that colors green while the window is open, and the gradient
      // filled one is the inner button, recolored per config.
      const $base = cloneSvg("timing-button");
      $base.find("circle[stroke]:not([stroke-dasharray])").attr("class", "ring");
      $base.find("circle[stroke-dasharray]").attr("class", "zone-outline");
      $base.find('circle[fill="black"]').attr("class", "zone");
      const $inner = $base.find('circle[fill^="url("]').attr("class", "inner");
      if (button) setFillColors($base, $inner, button.color);

      $press.find(".base").append($base);
      $press.find(".state.success").append(cloneSvg("button-success"));
      $press.find(".state.fail").append(cloneSvg("button-error"));

      $wrap.append($press);
    });

    this.$host.append($wrap);
    this.$presses = $wrap.find(".timing-press");
  }

  nextPress() {
    this.pressIndex++;

    if (this.pressIndex >= this.presses.length) {
      this.complete();
      return;
    }

    const press = this.presses[this.pressIndex];
    const $press = this.$presses.eq(this.pressIndex);
    $press.addClass("active");

    this.waitingForPress = true;
    this.inWindow = false;

    // No duration means untimed: there is no timing window to hit (the ring
    // is hidden since build), the zone stays black and any correct button
    // press is valid whenever it comes.
    if (press.duration === undefined) return;

    // Before the game's start signal the press just waits, start launches the
    // shrink then.
    if (this.shrinkUnlocked) this.startShrink(press, $press);
  }

  // The ring closes in over the press duration, opening the window once it
  // enters the zone circle. Reaching the inner circle without a press is a
  // miss.
  startShrink(press, $press) {
    this.shrinkTween = gsap.to($press.find(".ring"), {
      scale: this.innerRatio,
      transformOrigin: "50% 50%",
      duration: press.duration,
      ease: "none",
      onUpdate: () => {
        const scale = 1 - this.shrinkTween.progress() * (1 - this.innerRatio);
        this.setWindow($press, scale <= this.successRatio);
      },
      onComplete: () => this.resolvePress(false)
    });
  }

  // Opens or closes the success window: the zone circle colors green through
  // CSS while it is open.
  setWindow($press, open) {
    if (open === this.inWindow) return;
    this.inWindow = open;
    $press.toggleClass("in-window", open);
  }

  onButtonPress(action) {
    if (!this.waitingForPress) return;

    // Any press resolves the window: the right button while the zone is
    // green succeeds, too early or a wrong button fails. An untimed press
    // has no window, only the button has to be right.
    const press = this.presses[this.pressIndex];
    const windowOk = press.duration === undefined || this.inWindow;
    this.resolvePress(action === press.button && windowOk);
  }

  resolvePress(success) {
    if (!this.waitingForPress) return;
    this.waitingForPress = false;

    if (this.shrinkTween) {
      this.shrinkTween.kill();
      this.shrinkTween = null;
    }

    const press = this.presses[this.pressIndex];
    const $press = this.$presses.eq(this.pressIndex);
    // On a failure the zone returns to black; on a success the overlay covers
    // the press, the green can stay underneath.
    if (!success) this.setWindow($press, false);
    $press.removeClass("active").addClass(success ? "success" : "fail");
    if (success) this.points++;

    // The ring running out without a press sounds like a wrong one.
    this.playInputSound(success);

    // A press naming a gauge state moves the gauge as soon as it resolves,
    // hit or missed, instead of waiting for the end of the action.
    this.setGaugeState(press.gauge);

    // The verdict shows together with the last press's state: every press hit
    // gets the success message, any miss the error one.
    if (this.pressIndex >= this.presses.length - 1) {
      this.showVerdict(this.points === this.maxPoints);
    }

    // A press can bring its own top instructions. They change right away, hit
    // or missed, and the press only opens once they have had time to be read.
    const nextPress = this.presses[this.pressIndex + 1];
    const readSec = nextPress ? this.game.swapInstructions(nextPress.instruction, nextPress.subtext) : 0;

    // Let the state read for a beat before moving on.
    this.holdCall = gsap.delayedCall(Math.max(this.game.stateHoldSec, readSec), () => this.nextPress());
  }

  destroy() {
    if (this.shrinkTween) this.shrinkTween.kill();
    if (this.holdCall) this.holdCall.kill();
  }
}
